import { RefObject, useEffect, useRef } from 'react';

const DISTANCE = 80;
const AXIS_LOCK_DISTANCE = 12;
const MAX_DURATION = 800;
const AXIS_RATIO = 1.5;
const WHEEL_IDLE = 250;
const WHEEL_DISTANCE = 120;
const INTERACTIVE = 'button, a, input, textarea, select, [contenteditable], [role="slider"]';

export interface VerticalGestureFeedback {
  onStart?: () => boolean | void;
  onProgress?: (distance: number) => void;
  onCancel?: VoidFunction;
}

// Let nested scrollers consume the gesture before considering page navigation.
const canScroll = (target: HTMLElement, root: HTMLElement, direction: 'up' | 'down') => {
  let element: HTMLElement | null = target;
  while (element && root.contains(element)) {
    const overflow = window.getComputedStyle(element).overflowY;
    if (/auto|scroll/.test(overflow) && element.scrollHeight > element.clientHeight + 1) {
      if (direction === 'down' ? element.scrollTop > 1 :
        element.scrollTop + element.clientHeight < element.scrollHeight - 1) return true;
    }
    if (element === root) break;
    element = element.parentElement;
  }
  return false;
};

export default function useVerticalNavigationGesture(
  ref: RefObject<HTMLElement>,
  direction: 'up' | 'down',
  onNavigate?: VoidFunction,
  canStart?: () => boolean,
  feedback?: VerticalGestureFeedback
) {
  const callbacks = useRef({ onNavigate, canStart, feedback });
  callbacks.current = { onNavigate, canStart, feedback };
  const enabled = Boolean(onNavigate);
  useEffect(() => {
    const root = ref.current;
    if (!root || !enabled) return;
    // The PDP route wrapper uses display: contents. Capture return gestures on
    // its document, while accepting starts only from this active route subtree.
    const touchTarget = direction === 'down' ? root.ownerDocument : root;
    const captureTouch = direction === 'down';
    const canStart = () => callbacks.current.canStart?.() !== false;
    const onNavigate = () => callbacks.current.onNavigate?.();
    let start: { x: number; y: number; time: number; target: HTMLElement } | undefined;
    let distance = 0;
    let committed = false;
    let wheelDistance = 0;
    let lastWheelTime = 0;
    let wheelAllowed = false;
    let claimed = false;
    let wheelTimer: ReturnType<typeof setTimeout> | undefined;

    const cancel = () => {
      if (claimed) callbacks.current.feedback?.onCancel?.();
      claimed = false;
    };
    const progress = (value: number) => {
      if (!claimed) {
        if (callbacks.current.feedback?.onStart?.() === false) return false;
        claimed = true;
      }
      callbacks.current.feedback?.onProgress?.(value);
      return true;
    };

    const reset = () => { start = undefined; distance = 0; };
    const onStart = (event: TouchEvent) => {
      if (!(event.target instanceof Element) || !root.contains(event.target)) return;
      clearTimeout(wheelTimer);
      lastWheelTime = 0;
      wheelDistance = 0;
      cancel();
      reset();
      const target = event.target as HTMLElement;
      if (committed || event.touches.length !== 1 || target.closest(INTERACTIVE) ||
        (canStart && !canStart())) return;
      start = { x: event.touches[0].clientX, y: event.touches[0].clientY,
        time: Date.now(), target };
    };
    const onMove = (event: TouchEvent) => {
      if (!start) return;
      if (event.touches.length !== 1) { cancel(); reset(); return; }
      const dx = event.touches[0].clientX - start.x;
      const dy = event.touches[0].clientY - start.y;
      if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_LOCK_DISTANCE) {
        // Reserve the first downward move at the PDP boundary before the browser
        // starts native overscroll. Keep animation/navigation behind their thresholds.
        if (direction === 'down' && dy > 0 && dy >= Math.abs(dx) * AXIS_RATIO &&
          canStart() && !canScroll(start.target, root, direction) && event.cancelable) {
          event.preventDefault();
        }
        return;
      }
      if (Math.abs(dy) < Math.abs(dx) * AXIS_RATIO ||
        (direction === 'up' ? dy >= 0 : dy <= 0) ||
        (!claimed && canScroll(start.target, root, direction)) || (canStart && !canStart())) {
        cancel();
        reset();
        return;
      }
      // Cancel only a claimed vertical gesture, including native pull-to-refresh.
      if (!event.cancelable) { cancel(); reset(); return; }
      event.preventDefault();
      distance = Math.abs(dy);
      if (!progress(distance)) reset();
    };
    const onEnd = () => {
      const shouldNavigate = claimed && start && distance >= DISTANCE && Date.now() - start.time <= MAX_DURATION;
      reset();
      if (shouldNavigate) { claimed = false; committed = true; onNavigate(); }
      else cancel();
    };
    const onWheel = (event: WheelEvent) => {
      const target = event.target as HTMLElement;
      const now = Date.now();
      const eligible = !committed && !event.ctrlKey && !target.closest(INTERACTIVE) &&
        (!canStart || canStart()) && !canScroll(target, root, direction);
      // A scroll that started within PDP content must not become Back when it reaches the top.
      if (!lastWheelTime || now - lastWheelTime > WHEEL_IDLE) {
        cancel();
        wheelDistance = 0;
        wheelAllowed = eligible;
      }
      lastWheelTime = now;
      if (!eligible || Math.abs(event.deltaY) < Math.abs(event.deltaX) * AXIS_RATIO ||
        (direction === 'up' ? event.deltaY <= 0 : event.deltaY >= 0)) {
        wheelAllowed = false;
        cancel();
        return;
      }
      if (!wheelAllowed) return;
      // Chrome can deliver non-cancelable wheel events at the page boundary.
      // Cancellation support must not determine whether the gesture is recognized.
      if (event.cancelable) event.preventDefault();
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
      wheelDistance += Math.abs(event.deltaY) * unit;
      clearTimeout(wheelTimer);
      if (!progress(Math.min(wheelDistance, WHEEL_DISTANCE))) {
        wheelAllowed = false;
        return;
      }
      if (wheelDistance >= WHEEL_DISTANCE) {
        claimed = false;
        committed = true;
        onNavigate();
      } else wheelTimer = setTimeout(cancel, WHEEL_IDLE);
    };
    const onPageShow = () => {
      committed = false;
      lastWheelTime = 0;
      wheelDistance = 0;
      reset();
    };
    const onCancel = () => { cancel(); reset(); };
    const onTouchStart = (event: Event) => onStart(event as TouchEvent);
    const onTouchMove = (event: Event) => onMove(event as TouchEvent);
    touchTarget.addEventListener('touchstart', onTouchStart, { passive: true, capture: captureTouch });
    touchTarget.addEventListener('touchmove', onTouchMove, { passive: false, capture: captureTouch });
    touchTarget.addEventListener('touchend', onEnd, captureTouch);
    touchTarget.addEventListener('touchcancel', onCancel, captureTouch);
    root.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('pageshow', onPageShow);
    return () => {
      touchTarget.removeEventListener('touchstart', onTouchStart, captureTouch);
      touchTarget.removeEventListener('touchmove', onTouchMove, captureTouch);
      touchTarget.removeEventListener('touchend', onEnd, captureTouch);
      touchTarget.removeEventListener('touchcancel', onCancel, captureTouch);
      root.removeEventListener('wheel', onWheel);
      window.removeEventListener('pageshow', onPageShow);
      clearTimeout(wheelTimer);
    };
  }, [ref, direction, enabled]);
}
